import { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { graphqlSdk } from '../../../../Utils/graphql-client';
import { useElements } from '@stripe/react-stripe-js';
import useTypeSafeLocalize from '../../../../Hooks/use-type-safe-localize';
import {
  ErrorLocalization,
  LocalizationCategory,
  isErrorLocalization,
} from '../../../../localizations/types';
import { bookingState } from '../../client-state/booking-state-context';
import { errorReducerActionTypes } from '../../../../redusers/error-reducer';
import { vouchersReducerActionTypes } from '../../../../redusers/vouchers-reducer';
import { loadingReducerActionTypes } from '../../../../redusers/loading-reducer';
import Voucher, { VoucherProps } from '../../../../ui-components/Voucher';

const Wrapper = styled.div`
  margin: 24px 0 0;
`;

const VoucherWrapper = () => {
  const { state, dispatch } = useContext(bookingState);
  const localize = useTypeSafeLocalize();
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [loading, setLoading] = useState(false);
  const elements = useElements();

  const applyVoucher = async (voucherCode: string) => {
    if (state.booking.id && state.payment.paymentIntentClientSecret) {
      dispatch({ type: errorReducerActionTypes.VOUCHER, payload: '' });

      try {
        setLoading(true);
        dispatch({ type: loadingReducerActionTypes.VOUCHERS, payload: true });
        const res = await graphqlSdk.applyVoucherToBooking({
          bookingId: state.booking.id,
          voucherCode,
          paymentIntentId: state.payment.paymentIntentClientSecret.replace(
            /_secret_.+/,
            ''
          ),
        });

        setLoading(false);
        elements?.fetchUpdates();
        setVoucherOpen(false);
        setVoucherCode('');
        return dispatch({
          type: vouchersReducerActionTypes.UPDATE_VOUCHERS,
          payload: res.applyVoucherToBooking.vouchers,
        });
      } catch (error) {
        setLoading(false);
        const rawError = String((error as any).response?.errors?.[0]?.message);
        const rawErrorCode = String(
          (error as any).response?.errors?.[0]?.extensions?.code
        );
        if (isErrorLocalization(rawError)) {
          const errorMessage = localize(LocalizationCategory.error, rawError);
          return dispatch({
            type: errorReducerActionTypes.VOUCHER,
            payload: errorMessage,
          });
        } else if (isErrorLocalization(rawErrorCode)) {
          const errorMessage = localize(LocalizationCategory.error, rawErrorCode);
          return dispatch({
            type: errorReducerActionTypes.VOUCHER,
            payload: errorMessage,
          });
        } else {
          return dispatch({
            type: errorReducerActionTypes.VOUCHER,
            payload: localize(LocalizationCategory.error, ErrorLocalization.UnknownError),
          });
        }
      }
    }
  };

  const voucherProps: VoucherProps = {
    applyVoucher: () => applyVoucher(voucherCode),
    isOpen: voucherOpen,
    onOpen: (open: boolean) => {
      setVoucherOpen(open);
      if (!open) {
        dispatch({ type: errorReducerActionTypes.VOUCHER, payload: '' });
        setVoucherCode('');
      }
    },
    code: voucherCode,
    onChange: (event) => setVoucherCode(event.target.value),
    vouchers: state.vouchers,
    loading,
    error: state.errors.voucher ?? undefined,
  };

  return (
    <Wrapper>
      <Voucher {...voucherProps} />
    </Wrapper>
  );
};

export default VoucherWrapper;
